import { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider, Grid,
  FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Paper, Alert, Chip,
} from '@mui/material';
import {
  buildOutstationPricingGroupCode,
  getDuplicateOutstationPricingMessage,
  getOverlappingOutstationPricingMessage,
  validateOutstationGstPercentage,
  validateOutstationMinimumKm,
  validateOutstationMaximumKm,
  validateOutstationBaseKm,
  normalizeOutstationMaximumKm,
  normalizeOutstationBaseKm,
  validateOutstationSurgeMultiplier,
  normalizeOutstationSurgeMultiplier,
  validateOutstationVehicleBaseFare,
  normalizeOutstationVehicleBaseFare,
  validateOutstationVehiclePerKmRate,
  normalizeOutstationVehiclePerKmRate,
  validateOutstationVehicleBata,
  normalizeOutstationVehicleBata,
  validateOutstationVehicleNightCharge,
  normalizeOutstationVehicleNightCharge,
  validateOutstationVehicleWaitingCharge,
  normalizeOutstationVehicleWaitingCharge,
  outstationBaseKmHelper,
  calculateOutstationFare,
  resolveOutstationZoneCurrency,
  formatOutstationChargeTypeFieldLabel,
  outstationChargeTypeHelper,
  type OutstationChargeType,
  type OutstationPricingGroup,
  type OutstationVehiclePrice,
  type OutstationFareBreakdown,
  type OutstationZoneRow,
} from '../../utils/outstationPricing';
import {
  OUTSTATION_GST_PERCENT_DEFAULT,
  OUTSTATION_GST_PERCENT_MAX,
  OUTSTATION_GST_PERCENT_MIN,
  OUTSTATION_FARE_PREVIEW_WAITING_MINUTES,
  OUTSTATION_KM_SLAB_MIN,
  OUTSTATION_KM_SLAB_MAX,
  OUTSTATION_KM_SLAB_UNLIMITED,
  OUTSTATION_SURGE_MULTIPLIER_DEFAULT,
  ZONE_PRICE_MAX,
  ZONE_SURGE_MULTIPLIER_MAX,
  ZONE_SURGE_MULTIPLIER_MIN,
} from '../../constants/transportPartnerFlow';
import { alpha } from '@mui/material/styles';
import { vehiclesForOutstation } from '../../utils/masterRows';
import { formatMoney, chargeFieldLabel } from '../../utils/currency';

interface VehicleOption { code: string; name: string; serviceTypes?: string }

export interface OutstationPricingFormState {
  zoneCode: string;
  tripType: 'one_way' | 'round_trip';
  minimumKm: number;
  maximumKm: number;
  baseKm: number;
  pricingGroupCode: string;
  gstPercentage: number;
  tollType: 'included' | 'actual';
  parkingType: 'included' | 'actual';
  surgeEnabled: boolean;
  surgeMultiplier: number;
  vehiclePrices: OutstationVehiclePrice[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  editingGroup: OutstationPricingGroup | null;
  existingGroups: OutstationPricingGroup[];
  zones: OutstationZoneRow[];
  vehicles: VehicleOption[];
  lockedZoneCode?: string;
  defaultCurrency?: string;
  resolvePricingZoneCode?: (raw: string | null | undefined) => string;
  loading?: boolean;
  onSave: (form: OutstationPricingFormState, editingGroup: OutstationPricingGroup | null) => void;
}

type VehicleFormRow = {
  baseFare: string;
  perKmRate: string;
  driverBataPerDay: string;
  nightCharge: string;
  waitingChargePerMinute: string;
};

function FareBreakdownPreview({
  title, breakdown, currency,
}: { title: string; breakdown: OutstationFareBreakdown; currency: string }) {
  const chargeLines = breakdown.lines.filter((line) => !line.id.endsWith('_policy'));
  const policyLines = breakdown.lines.filter((line) => line.id.endsWith('_policy'));

  return (
    <Box>
      <Typography variant="body2" fontWeight={700} color="text.primary" gutterBottom>
        {title}
      </Typography>
      <Stack spacing={0.5}>
        {chargeLines.map((line) => (
          <Stack key={line.id} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.primary" sx={{ flex: 1 }}>
              {line.label}: {line.formula}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
              {formatMoney(line.amount, currency)}
            </Typography>
          </Stack>
        ))}
        <Divider sx={{ my: 0.75 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600} color="text.primary">Subtotal</Typography>
          <Typography variant="body2" fontWeight={700} color="text.primary">{formatMoney(breakdown.subtotal, currency)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600} color="text.primary">GST</Typography>
          <Typography variant="body2" fontWeight={700} color="text.primary">{formatMoney(breakdown.gst, currency)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={800} color="primary.main">Quoted vehicle fare</Typography>
          <Typography variant="body2" fontWeight={800} color="primary.main">{formatMoney(breakdown.quotedVehicleTotal, currency)}</Typography>
        </Stack>
        {policyLines.map((line) => (
          <Typography key={line.id} variant="body2" color="info.dark" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
            {line.label}: {line.formula}
          </Typography>
        ))}
        {(breakdown.tollType === 'actual' || breakdown.parkingType === 'actual') && (
          <Typography variant="caption" color="warning.dark" fontWeight={600} display="block">
            Final bill = quoted vehicle fare
            {breakdown.tollType === 'actual' ? ' + toll (actual)' : ''}
            {breakdown.parkingType === 'actual' ? ' + parking (actual)' : ''}
          </Typography>
        )}
        {(breakdown.tollType === 'included' && breakdown.parkingType === 'included') && (
          <Typography variant="caption" color="success.dark" fontWeight={600} display="block">
            Toll and parking are included — no separate toll/parking line on the final bill.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function OutstationChargeTypeSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: OutstationChargeType;
  onChange: (value: OutstationChargeType) => void;
}) {
  const excluded = value === 'actual';
  return (
    <Box>
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as OutstationChargeType)}
        SelectProps={{
          renderValue: (selected) => formatOutstationChargeTypeFieldLabel(label, selected as OutstationChargeType),
        }}
      >
        <MenuItem value="included">Included in fare</MenuItem>
        <MenuItem value="actual">Excluded (actual cost)</MenuItem>
      </TextField>
      <Chip
        size="small"
        sx={{ mt: 0.75 }}
        label={outstationChargeTypeHelper(value, label)}
        color={excluded ? 'warning' : 'success'}
        variant={excluded ? 'filled' : 'outlined'}
      />
    </Box>
  );
}

export default function OutstationPricingDrawer({
  open, onClose, editingGroup, existingGroups, zones, vehicles, lockedZoneCode,
  defaultCurrency, resolvePricingZoneCode, loading, onSave,
}: Props) {
  const [zoneCode, setZoneCode] = useState('');
  const [tripType, setTripType] = useState<'one_way' | 'round_trip'>('one_way');
  const [minimumKmInput, setMinimumKmInput] = useState('');
  const [maximumKmInput, setMaximumKmInput] = useState('');
  const [baseKmInput, setBaseKmInput] = useState('');
  const [gstInput, setGstInput] = useState(String(OUTSTATION_GST_PERCENT_DEFAULT));
  const [tollType, setTollType] = useState<'included' | 'actual'>('included');
  const [parkingType, setParkingType] = useState<'included' | 'actual'>('included');
  const [surgeEnabled, setSurgeEnabled] = useState(false);
  const [surgeMultiplierInput, setSurgeMultiplierInput] = useState(String(OUTSTATION_SURGE_MULTIPLIER_DEFAULT));
  const [vehicleRows, setVehicleRows] = useState<Record<string, VehicleFormRow>>({});
  const [applyBaseFare, setApplyBaseFare] = useState('');
  const [applyPerKm, setApplyPerKm] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const outstationVehicles = useMemo(() => vehiclesForOutstation(vehicles), [vehicles]);

  const zoneCurrency = useMemo(() => {
    const resolver = resolvePricingZoneCode || ((code: string | null | undefined) => String(code || ''));
    const code = lockedZoneCode || zoneCode;
    if (!code) return defaultCurrency || '';
    return resolveOutstationZoneCurrency(code, zones, resolver);
  }, [zones, zoneCode, lockedZoneCode, defaultCurrency, resolvePricingZoneCode]);

  const amountLabel = (label: string) => chargeFieldLabel(label, zoneCurrency);

  useEffect(() => {
    if (!open) return;
    setShowValidation(false);
    if (editingGroup) {
      setZoneCode(editingGroup.zoneCode);
      setTripType(editingGroup.tripType);
      setMinimumKmInput(String(editingGroup.minimumKm));
      setMaximumKmInput(
        editingGroup.maximumKm === OUTSTATION_KM_SLAB_UNLIMITED ? '' : String(editingGroup.maximumKm),
      );
      setBaseKmInput(String(editingGroup.baseKm || ''));
      setGstInput(String(editingGroup.gstPercentage));
      setTollType(editingGroup.tollType);
      setParkingType(editingGroup.parkingType);
      setSurgeEnabled(editingGroup.surgeEnabled);
      setSurgeMultiplierInput(String(
        editingGroup.surgeEnabled && editingGroup.surgeMultiplier > ZONE_SURGE_MULTIPLIER_MIN
          ? editingGroup.surgeMultiplier
          : OUTSTATION_SURGE_MULTIPLIER_DEFAULT,
      ));
      const next: Record<string, VehicleFormRow> = {};
      for (const v of outstationVehicles) {
        const match = editingGroup.vehiclePrices.find((p) => p.vehicleTypeCode === v.code);
        next[v.code] = {
          baseFare: match ? String(match.baseFare) : '',
          perKmRate: match ? String(match.perKmRate) : '',
          driverBataPerDay: match ? String(match.driverBataPerDay) : '',
          nightCharge: match ? String(match.nightCharge) : '',
          waitingChargePerMinute: match ? String(match.waitingChargePerMinute) : '',
        };
      }
      setVehicleRows(next);
    } else {
      setZoneCode(lockedZoneCode || zones[0]?.code || '');
      setTripType('one_way');
      setMinimumKmInput('');
      setMaximumKmInput('');
      setBaseKmInput('');
      setGstInput(String(OUTSTATION_GST_PERCENT_DEFAULT));
      setTollType('included');
      setParkingType('included');
      setSurgeEnabled(false);
      setSurgeMultiplierInput(String(OUTSTATION_SURGE_MULTIPLIER_DEFAULT));
      const next: Record<string, VehicleFormRow> = {};
      for (const v of outstationVehicles) {
        next[v.code] = {
          baseFare: '',
          perKmRate: '',
          driverBataPerDay: '',
          nightCharge: '',
          waitingChargePerMinute: '',
        };
      }
      setVehicleRows(next);
    }
    setApplyBaseFare('');
    setApplyPerKm('');
  }, [open, editingGroup, outstationVehicles, zones, lockedZoneCode]);

  const patchVehicle = (code: string, field: keyof VehicleFormRow, value: string) => {
    setVehicleRows((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const applyToAllVehicles = () => {
    setVehicleRows((prev) => {
      const next = { ...prev };
      for (const v of outstationVehicles) {
        next[v.code] = {
          ...next[v.code],
          ...(applyBaseFare ? { baseFare: applyBaseFare } : {}),
          ...(applyPerKm ? { perKmRate: applyPerKm } : {}),
        };
      }
      return next;
    });
  };

  const gstError = validateOutstationGstPercentage(gstInput);
  const gstPercentage = gstError ? OUTSTATION_GST_PERCENT_DEFAULT : Number(gstInput);

  const minKmError = editingGroup ? undefined : validateOutstationMinimumKm(minimumKmInput);
  const minimumKm = editingGroup
    ? editingGroup.minimumKm
    : (minKmError ? 0 : Number(minimumKmInput));
  const maximumKm = editingGroup
    ? editingGroup.maximumKm
    : normalizeOutstationMaximumKm(maximumKmInput);
  const maxKmError = editingGroup
    ? undefined
    : validateOutstationMaximumKm(minKmError ? OUTSTATION_KM_SLAB_MIN : minimumKm, maximumKmInput);
  const maxKmHelper = maxKmError
    ? undefined
    : (maximumKmInput === '' || maximumKmInput === '0'
      ? '0 or empty = unlimited (no upper km limit)'
      : `Must be ≥ ${minimumKm} km`);
  const slabMaximumKm = editingGroup
    ? editingGroup.maximumKm
    : (maximumKmInput === '' || maximumKmInput === '0'
      ? OUTSTATION_KM_SLAB_UNLIMITED
      : Number(maximumKmInput) || OUTSTATION_KM_SLAB_UNLIMITED);
  const slabMinimumKm = editingGroup ? editingGroup.minimumKm : (minKmError ? 0 : minimumKm);
  const baseKm = baseKmInput === '' ? 0 : Number(baseKmInput);
  const baseKmBounds = { minimumKm: slabMinimumKm, maximumKm: slabMaximumKm };
  const baseKmError = validateOutstationBaseKm(baseKmInput, baseKmBounds);
  const baseKmHelper = outstationBaseKmHelper(baseKmBounds);
  const baseKmInputMax = slabMaximumKm > OUTSTATION_KM_SLAB_UNLIMITED
    ? slabMaximumKm
    : OUTSTATION_KM_SLAB_MAX;
  const surgeMultiplierError = validateOutstationSurgeMultiplier(
    surgeMultiplierInput,
    { required: surgeEnabled },
  );
  const surgeMultiplier = surgeEnabled
    ? (surgeMultiplierError ? OUTSTATION_SURGE_MULTIPLIER_DEFAULT : Number(surgeMultiplierInput))
    : ZONE_SURGE_MULTIPLIER_MIN;

  const handleSurgeToggle = (checked: boolean) => {
    if (!checked && surgeEnabled) {
      const current = Number(surgeMultiplierInput);
      if (Number.isFinite(current) && current > ZONE_SURGE_MULTIPLIER_MIN) {
        const confirmed = window.confirm(
          'Disable surge pricing? The configured surge multiplier will be cleared.',
        );
        if (!confirmed) return;
      }
      setSurgeMultiplierInput(String(OUTSTATION_SURGE_MULTIPLIER_DEFAULT));
    } else if (checked && !surgeEnabled) {
      setSurgeMultiplierInput(String(OUTSTATION_SURGE_MULTIPLIER_DEFAULT));
    }
    setSurgeEnabled(checked);
  };

  const vehicleBaseFareErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const v of outstationVehicles) {
      errors[v.code] = validateOutstationVehicleBaseFare(vehicleRows[v.code]?.baseFare, { required: true });
    }
    return errors;
  }, [outstationVehicles, vehicleRows]);

  const vehiclePerKmErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const v of outstationVehicles) {
      errors[v.code] = validateOutstationVehiclePerKmRate(vehicleRows[v.code]?.perKmRate, { required: true });
    }
    return errors;
  }, [outstationVehicles, vehicleRows]);

  const vehicleBataErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const v of outstationVehicles) {
      const row = vehicleRows[v.code];
      const hasBataInput = row?.driverBataPerDay !== '' && row?.driverBataPerDay != null;
      if (hasBataInput) {
        errors[v.code] = validateOutstationVehicleBata(row?.driverBataPerDay);
      }
    }
    return errors;
  }, [outstationVehicles, vehicleRows]);

  const vehicleNightChargeErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const v of outstationVehicles) {
      const row = vehicleRows[v.code];
      const hasNightInput = row?.nightCharge !== '' && row?.nightCharge != null;
      if (hasNightInput) {
        errors[v.code] = validateOutstationVehicleNightCharge(row?.nightCharge);
      }
    }
    return errors;
  }, [outstationVehicles, vehicleRows]);

  const vehicleWaitingChargeErrors = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    for (const v of outstationVehicles) {
      const row = vehicleRows[v.code];
      const hasWaitingInput = row?.waitingChargePerMinute !== '' && row?.waitingChargePerMinute != null;
      if (hasWaitingInput) {
        errors[v.code] = validateOutstationVehicleWaitingCharge(row?.waitingChargePerMinute);
      }
    }
    return errors;
  }, [outstationVehicles, vehicleRows]);

  const allVehiclePricingValid = outstationVehicles.length > 0
    && outstationVehicles.every((v) => (
      !vehicleBaseFareErrors[v.code]
      && !vehiclePerKmErrors[v.code]
      && !vehicleBataErrors[v.code]
      && !vehicleNightChargeErrors[v.code]
      && !vehicleWaitingChargeErrors[v.code]
    ));

  const vehiclePrices: OutstationVehiclePrice[] = allVehiclePricingValid
    ? outstationVehicles.map((v) => ({
      vehicleTypeCode: v.code,
      vehicleTypeName: v.name,
      baseFare: normalizeOutstationVehicleBaseFare(vehicleRows[v.code]?.baseFare),
      perKmRate: normalizeOutstationVehiclePerKmRate(vehicleRows[v.code]?.perKmRate),
      driverBataPerDay: normalizeOutstationVehicleBata(vehicleRows[v.code]?.driverBataPerDay),
      nightCharge: normalizeOutstationVehicleNightCharge(vehicleRows[v.code]?.nightCharge),
      waitingChargePerMinute: normalizeOutstationVehicleWaitingCharge(vehicleRows[v.code]?.waitingChargePerMinute),
      rowId: editingGroup?.vehiclePrices.find((p) => p.vehicleTypeCode === v.code)?.rowId,
      rowCode: editingGroup?.vehiclePrices.find((p) => p.vehicleTypeCode === v.code)?.rowCode,
    }))
    : [];

  const previewVehicle = vehiclePrices[0];
  const farePreview = useMemo(() => {
    if (!previewVehicle || gstError || baseKmError) return null;
    const withinKm = baseKm > 0 ? baseKm : 1;
    const beyondKm = baseKm + 50;
    const previewWaitingMinutes = previewVehicle.waitingChargePerMinute > 0
      ? OUTSTATION_FARE_PREVIEW_WAITING_MINUTES
      : 0;
    const common = {
      baseKm,
      baseFare: previewVehicle.baseFare,
      perKmRate: previewVehicle.perKmRate,
      driverBataPerDay: previewVehicle.driverBataPerDay,
      nightCharge: previewVehicle.nightCharge,
      waitingMinutes: previewWaitingMinutes,
      waitingChargePerMinute: previewVehicle.waitingChargePerMinute,
      gstPercentage,
      surgeEnabled,
      surgeMultiplier,
      tollType,
      parkingType,
      currency: zoneCurrency,
    };
    return {
      vehicleName: previewVehicle.vehicleTypeName,
      previewWaitingMinutes,
      within: calculateOutstationFare({ ...common, totalKm: withinKm }),
      beyond: calculateOutstationFare({ ...common, totalKm: beyondKm }),
    };
  }, [previewVehicle, baseKm, gstPercentage, gstError, baseKmError, surgeEnabled, surgeMultiplier, tollType, parkingType, zoneCurrency]);

  const effectiveZoneCode = lockedZoneCode || zoneCode;
  const duplicateError = useMemo(() => {
    if (minKmError || maxKmError || !effectiveZoneCode) return undefined;
    return getDuplicateOutstationPricingMessage(existingGroups, {
      zoneCode: effectiveZoneCode,
      tripType,
      minimumKm,
      maximumKm,
      excludeGroupCode: editingGroup?.pricingGroupCode,
      resolveZoneCode: resolvePricingZoneCode,
    });
  }, [
    existingGroups,
    effectiveZoneCode,
    tripType,
    minimumKm,
    maximumKm,
    minKmError,
    maxKmError,
    editingGroup?.pricingGroupCode,
    resolvePricingZoneCode,
  ]);

  const overlapError = useMemo(() => {
    if (minKmError || maxKmError || !effectiveZoneCode || duplicateError) return undefined;
    return getOverlappingOutstationPricingMessage(existingGroups, {
      zoneCode: effectiveZoneCode,
      tripType,
      minimumKm,
      maximumKm,
      excludeGroupCode: editingGroup?.pricingGroupCode,
      resolveZoneCode: resolvePricingZoneCode,
    });
  }, [
    existingGroups,
    effectiveZoneCode,
    tripType,
    minimumKm,
    maximumKm,
    minKmError,
    maxKmError,
    duplicateError,
    editingGroup?.pricingGroupCode,
    resolvePricingZoneCode,
  ]);

  const canSave = !!zoneCode && allVehiclePricingValid && !gstError && !minKmError && !maxKmError && !baseKmError
    && !surgeMultiplierError && !duplicateError && !overlapError;

  const zoneError = !zoneCode ? 'Select a zone.' : undefined;

  const validationIssues = useMemo(() => {
    const issues: string[] = [];
    if (zoneError) issues.push(zoneError);
    if (!editingGroup && minKmError) issues.push(`Min KM: ${minKmError}`);
    if (!editingGroup && maxKmError) issues.push(`Max KM: ${maxKmError}`);
    if (baseKmError) issues.push(`Base KM: ${baseKmError}`);
    if (gstError) issues.push(`GST: ${gstError}`);
    if (surgeMultiplierError) issues.push(`Surge: ${surgeMultiplierError}`);
    if (duplicateError) issues.push(duplicateError);
    if (overlapError) issues.push(overlapError);
    for (const v of outstationVehicles) {
      const label = v.name;
      if (vehicleBaseFareErrors[v.code]) issues.push(`${label} — Base fare: ${vehicleBaseFareErrors[v.code]}`);
      if (vehiclePerKmErrors[v.code]) issues.push(`${label} — Per km: ${vehiclePerKmErrors[v.code]}`);
      if (vehicleBataErrors[v.code]) issues.push(`${label} — Bata: ${vehicleBataErrors[v.code]}`);
      if (vehicleNightChargeErrors[v.code]) issues.push(`${label} — Night: ${vehicleNightChargeErrors[v.code]}`);
      if (vehicleWaitingChargeErrors[v.code]) {
        issues.push(`${label} — Wait/min: ${vehicleWaitingChargeErrors[v.code]}`);
      }
    }
    return issues;
  }, [
    zoneError,
    editingGroup,
    minKmError,
    maxKmError,
    baseKmError,
    gstError,
    surgeMultiplierError,
    duplicateError,
    overlapError,
    outstationVehicles,
    vehicleBaseFareErrors,
    vehiclePerKmErrors,
    vehicleBataErrors,
    vehicleNightChargeErrors,
    vehicleWaitingChargeErrors,
  ]);

  const fieldError = (message?: string) => (showValidation ? message : undefined);
  const fieldHasError = (message?: string) => showValidation && !!message;

  const handleSave = () => {
    if (!canSave) {
      setShowValidation(true);
      return;
    }
    const form: OutstationPricingFormState = {
      zoneCode,
      tripType,
      minimumKm,
      maximumKm,
      baseKm: normalizeOutstationBaseKm(baseKmInput, baseKmBounds),
      pricingGroupCode: editingGroup?.pricingGroupCode
        || buildOutstationPricingGroupCode(zoneCode, tripType, minimumKm, maximumKm),
      gstPercentage,
      tollType,
      parkingType,
      surgeEnabled,
      surgeMultiplier: normalizeOutstationSurgeMultiplier(surgeMultiplierInput, surgeEnabled),
      vehiclePrices,
    };
    onSave(form, editingGroup);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={800}>
            {editingGroup ? 'Edit Outstation Pricing' : 'Add Outstation Pricing'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            KM slab + trip type with per-vehicle fares — same flow as rental management.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {showValidation && validationIssues.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Fix the following to save pricing
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {validationIssues.map((issue) => (
                  <Typography key={issue} component="li" variant="body2" sx={{ mb: 0.25 }}>
                    {issue}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" select label="Zone" required value={zoneCode}
                onChange={(e) => setZoneCode(e.target.value)} disabled={!!editingGroup || !!lockedZoneCode}
                error={fieldHasError(zoneError)}
                helperText={fieldError(zoneError)}>
                {zones.map((z) => (
                  <MenuItem key={z._id} value={z.code || ''}>{z.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" select label="Trip Type" value={tripType}
                onChange={(e) => setTripType(e.target.value as 'one_way' | 'round_trip')}
                disabled={!!editingGroup}>
                <MenuItem value="one_way">One Way</MenuItem>
                <MenuItem value="round_trip">Round Trip</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="GST %" required value={gstInput}
                onChange={(e) => setGstInput(e.target.value)}
                error={fieldHasError(gstError)}
                helperText={fieldError(gstError) || `${OUTSTATION_GST_PERCENT_MIN}–${OUTSTATION_GST_PERCENT_MAX}%`}
                inputProps={{ min: OUTSTATION_GST_PERCENT_MIN, max: OUTSTATION_GST_PERCENT_MAX, step: 0.01 }} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} gutterBottom>KM Slab</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Min KM" required
                value={editingGroup ? minimumKm : minimumKmInput}
                onChange={(e) => setMinimumKmInput(e.target.value)}
                disabled={!!editingGroup}
                error={fieldHasError(minKmError)}
                helperText={fieldError(minKmError) || `Required · ${OUTSTATION_KM_SLAB_MIN}+ km`}
                inputProps={{ min: OUTSTATION_KM_SLAB_MIN, step: 1 }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Max KM"
                helperText={fieldError(maxKmError) || maxKmHelper}
                error={fieldHasError(maxKmError)}
                value={editingGroup
                  ? (editingGroup.maximumKm === OUTSTATION_KM_SLAB_UNLIMITED ? '' : editingGroup.maximumKm)
                  : maximumKmInput}
                onChange={(e) => setMaximumKmInput(e.target.value)}
                disabled={!!editingGroup}
                placeholder="Unlimited"
                inputProps={{
                  min: minKmError ? OUTSTATION_KM_SLAB_MIN : minimumKm,
                  step: 1,
                }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Base KM (included)"
                value={baseKmInput}
                onChange={(e) => setBaseKmInput(e.target.value)}
                error={fieldHasError(baseKmError)}
                helperText={fieldError(baseKmError) || baseKmHelper}
                inputProps={{ min: 0, max: baseKmInputMax, step: 1 }} />
            </Grid>
          </Grid>

          {duplicateError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {duplicateError}
            </Alert>
          )}

          {overlapError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {overlapError}
            </Alert>
          )}

          <Alert
            severity="info"
            variant="standard"
            sx={{
              mb: 2,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
              color: 'text.primary',
              border: 1,
              borderColor: 'info.main',
              '& .MuiAlert-icon': { color: 'info.dark' },
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} gutterBottom sx={{ color: 'info.dark' }}>
              Quoted fare formula
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', lineHeight: 1.65 }}>
              Quoted fare = base amount + (chargeable km × per km rate) + driver bata + night charge + waiting charge
              {surgeEnabled ? ' × surge multiplier' : ''} + GST.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', lineHeight: 1.65, fontWeight: 500 }}>
              Chargeable km = total trip km − Base KM (minimum 0). No per-km charge applies within Base KM.
              {(tollType === 'actual' || parkingType === 'actual') && (
                <> Toll and parking marked as excluded are billed separately at actual cost — not included in the quoted fare above.</>
              )}
            </Typography>
          </Alert>

          {farePreview && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                borderColor: 'primary.main',
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} color="text.primary" sx={{ mb: 1.5, display: 'block' }}>
                Fare preview — {farePreview.vehicleName} (Base KM = {baseKm} km included)
              </Typography>
              <Stack spacing={2} divider={<Divider flexItem />}>
                <FareBreakdownPreview
                  title={`Within base (${farePreview.within.totalKm} km trip)`}
                  breakdown={farePreview.within}
                  currency={zoneCurrency}
                />
                <FareBreakdownPreview
                  title={`Beyond base (${farePreview.beyond.totalKm} km trip${farePreview.previewWaitingMinutes > 0 ? ` · ${farePreview.previewWaitingMinutes} min waiting in preview` : ''})`}
                  breakdown={farePreview.beyond}
                  currency={zoneCurrency}
                />
              </Stack>
            </Paper>
          )}

          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Toll &amp; Parking</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <OutstationChargeTypeSelect label="Toll" value={tollType} onChange={setTollType} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <OutstationChargeTypeSelect label="Parking" value={parkingType} onChange={setParkingType} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Surge</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={surgeEnabled}
                  onChange={(e) => handleSurgeToggle(e.target.checked)}
                  inputProps={{ 'aria-label': surgeEnabled ? 'Surge enabled' : 'Surge disabled' }}
                />
              )}
              label={(
                <Typography variant="body2" fontWeight={600} component="span">
                  Surge {surgeEnabled ? 'Enabled' : 'Disabled'}
                </Typography>
              )}
              sx={{
                mx: 0,
                mb: surgeEnabled ? 1 : 0,
                alignItems: 'center',
                '& .MuiFormControlLabel-label': { ml: 2 },
              }}
            />
            {surgeEnabled ? (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Surge multiplier"
                value={surgeMultiplierInput}
                onChange={(e) => setSurgeMultiplierInput(e.target.value)}
                error={fieldHasError(surgeMultiplierError)}
                helperText={
                  fieldError(surgeMultiplierError)
                  || `${ZONE_SURGE_MULTIPLIER_MIN}–${ZONE_SURGE_MULTIPLIER_MAX} · e.g. 1.25 adds 25% to the quoted fare before GST`
                }
                inputProps={{
                  min: ZONE_SURGE_MULTIPLIER_MIN,
                  max: ZONE_SURGE_MULTIPLIER_MAX,
                  step: 0.01,
                }}
                sx={{ mt: 1 }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Surge is off — quoted fare uses base + distance + extras without a multiplier.
              </Typography>
            )}
          </Paper>

          {(tollType === 'actual' || parkingType === 'actual') && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              {parkingType === 'actual' && (
                <Typography variant="body2" display="block">
                  <strong>Parking excluded:</strong> parking fees are not part of the quoted vehicle fare and will be added at actual cost on the final bill.
                </Typography>
              )}
              {tollType === 'actual' && (
                <Typography variant="body2" display="block" sx={{ mt: parkingType === 'actual' ? 0.75 : 0 }}>
                  <strong>Toll excluded:</strong> toll charges are not part of the quoted vehicle fare and will be added at actual cost on the final bill.
                </Typography>
              )}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Apply to all vehicles
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField size="small" type="number" label={amountLabel('Base Amount')} value={applyBaseFare}
                onChange={(e) => setApplyBaseFare(e.target.value)} sx={{ width: { sm: 140 } }} />
              <TextField size="small" type="number" label={amountLabel('Per KM')} value={applyPerKm}
                onChange={(e) => setApplyPerKm(e.target.value)} sx={{ width: { sm: 120 } }} />
              <Button size="small" variant="outlined" onClick={applyToAllVehicles}
                disabled={!applyBaseFare && !applyPerKm}>
                Apply to all
              </Button>
            </Stack>
          </Paper>

          {outstationVehicles.length > 0 && showValidation && !allVehiclePricingValid && (
            <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>
              Enter valid base fare and per km rate for every vehicle type. Optional charges must be valid when entered.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ overflow: 'visible', mb: 2 }}>
            <TableContainer sx={{ overflow: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{amountLabel('Base')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{amountLabel('/ KM')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{amountLabel('Bata')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{amountLabel('Night')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{amountLabel('Wait/min')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstationVehicles.map((v) => (
                  <TableRow key={v.code} sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{v.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{v.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        required
                        value={vehicleRows[v.code]?.baseFare || ''}
                        onChange={(e) => patchVehicle(v.code, 'baseFare', e.target.value)}
                        error={fieldHasError(vehicleBaseFareErrors[v.code])}
                        helperText={fieldError(vehicleBaseFareErrors[v.code])}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25, whiteSpace: 'normal', maxWidth: 140 } }}
                        inputProps={{ min: 0.01, step: 0.01 }}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        required
                        value={vehicleRows[v.code]?.perKmRate || ''}
                        onChange={(e) => patchVehicle(v.code, 'perKmRate', e.target.value)}
                        error={fieldHasError(vehiclePerKmErrors[v.code])}
                        helperText={fieldError(vehiclePerKmErrors[v.code])}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25, whiteSpace: 'normal', maxWidth: 140 } }}
                        inputProps={{ min: 0.01, max: ZONE_PRICE_MAX, step: 0.01 }}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={vehicleRows[v.code]?.driverBataPerDay || ''}
                        onChange={(e) => patchVehicle(v.code, 'driverBataPerDay', e.target.value)}
                        error={fieldHasError(vehicleBataErrors[v.code])}
                        helperText={fieldError(vehicleBataErrors[v.code])}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25, whiteSpace: 'normal', maxWidth: 140 } }}
                        inputProps={{ min: 0, max: ZONE_PRICE_MAX, step: 0.01 }}
                        placeholder="0"
                        sx={{ width: 112 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={vehicleRows[v.code]?.nightCharge || ''}
                        onChange={(e) => patchVehicle(v.code, 'nightCharge', e.target.value)}
                        error={fieldHasError(vehicleNightChargeErrors[v.code])}
                        helperText={fieldError(vehicleNightChargeErrors[v.code])}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25, whiteSpace: 'normal', maxWidth: 140 } }}
                        inputProps={{ min: 0, max: ZONE_PRICE_MAX, step: 0.01 }}
                        placeholder="0"
                        sx={{ width: 112 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={vehicleRows[v.code]?.waitingChargePerMinute || ''}
                        onChange={(e) => patchVehicle(v.code, 'waitingChargePerMinute', e.target.value)}
                        error={fieldHasError(vehicleWaitingChargeErrors[v.code])}
                        helperText={fieldError(vehicleWaitingChargeErrors[v.code])}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25, whiteSpace: 'normal', maxWidth: 140 } }}
                        inputProps={{ min: 0, max: ZONE_PRICE_MAX, step: 0.01 }}
                        placeholder="0"
                        sx={{ width: 112 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!outstationVehicles.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No outstation vehicle types found in Vehicle Type master.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={loading} onClick={handleSave}>
            {loading ? 'Saving…' : editingGroup ? 'Update Pricing' : 'Create Pricing'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
